/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import Select, { GroupBase, Options, OnChangeValue } from 'react-select';
import { Field, FieldRenderProps } from 'react-final-form';

export interface SelectItem {
  label: string;
  value: string;
}

export interface ReactSelectProps extends FieldRenderProps<OnChangeValue<SelectItem, boolean>, HTMLSelectElement> {
  options?: Array<SelectItem>;
  isMulti?: boolean;
  defaultValue?: Array<SelectItem>;
  getOptionLabel?: (option: unknown) => string;
  getOptionValue?: (option: unknown) => string;
  onChange?: (option: unknown) => void;
}

const customStyles = {
  multiValue: (styles: any) => {
    return {
      ...styles,
      maxWidth: 30,
      '& svg': {
        display: 'none',
      },
      ':hover': {
        maxWidth: 100,
        '& svg': {
          display: 'inherit',
        },
      },
    };
  },
  multiValueRemove: (styles: any) => ({
    ...styles,
    paddingLeft: 0,
  }),
  valueContainer: (styles: any) => {
    return {
      ...styles,
      paddingLeft: 2.5,
      flexWrap: 'nowrap',
      cursor: 'pointer',
    };
  },
};

const ReactSelectAdapter = ({
  input,
  meta,
  options,
  defaultValue,
  isMulti,
  multiValueCompact,
  getOptionLabel,
  getOptionValue,
  onChange,
  disabled,
}: ReactSelectProps): React.ReactElement => {
  const handleChange = (e: any) => {
    input.onChange(e);
    if (onChange) onChange(e);
  };

  const getStyles = () => {
    if (!multiValueCompact || (isMulti && (input?.value as SelectItem[])?.length === 1)) {
      return {
        multiValue: (styles: any) => {
          return {
            ...styles,
          };
        },
        multiValueRemove: (styles: any) => ({
          ...styles,
          paddingLeft: 0,
        }),
        valueContainer: (styles: any) => {
          return {
            ...styles,
            cursor: 'pointer',
          };
        },
        menuPortal: (base: any) => ({ ...base, zIndex: 2147483647 }),
      };
    }

    return customStyles;
  };

  return (
    <Select
      styles={getStyles()}
      menuPortalTarget={document.body}
      defaultValue={defaultValue}
      value={input.value}
      onChange={handleChange}
      {...meta}
      options={options}
      isMulti={isMulti}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      isDisabled={disabled}
    />
  );
};

type OptionTypeBase = { [key: string]: any };

export interface SelectFieldProps<
  OptionType extends OptionTypeBase = { label: string; value: string },
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>
> {
  name: string;
  label?: string;
  options?: ReadonlyArray<GroupType> | Options<OptionType>;
  isMulti?: boolean;
  multiValueCompact?: boolean;
  defaultValue?: Array<SelectItem>;
  className?: string;
  getOptionLabel?: (option: unknown) => string;
  getOptionValue?: (option: unknown) => string;
  onChange?: (option: unknown) => void;
  disabled?: boolean;
}

export const SelectField: React.FC<SelectFieldProps> = (props: SelectFieldProps): React.ReactElement => {
  const {
    name,
    label,
    options,
    defaultValue,
    isMulti,
    className,
    getOptionLabel,
    getOptionValue,
    onChange,
    multiValueCompact,
    disabled,
  } = props;
  return (
    <div className={`form-group ${className}`} id={`${name}-select`}>
      {label && (
        <div className="row">
          <div className="col">
            <label htmlFor={name}>{label}</label>
          </div>
        </div>
      )}
      <Field
        disabled={disabled}
        name={name}
        component={ReactSelectAdapter}
        options={options}
        isMulti={isMulti}
        multiValueCompact={multiValueCompact}
        defaultValue={defaultValue}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        onChange={onChange}
      />
    </div>
  );
};
